import React, { useState, useEffect } from 'react';
import { 
  Package, Search, Loader2, TruckIcon, RotateCcw, 
  XIcon, AlertTriangle, Check, Filter, Download, X, CheckCircle
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Order status types
export enum OrderStatus {
  PROCESSING = 'processing',
  DISPATCHED = 'dispatched',
  COMPLETE = 'complete',
  CANCELLED = 'cancelled',
  RETURN_INITIATED = 'return_initiated',
  REFUNDED = 'refunded'
}

// Item condition types for returns
export enum ItemCondition {
  EXCELLENT = 'excellent',
  GOOD = 'good',
  ACCEPTABLE = 'acceptable',
  DAMAGED = 'damaged',
  UNSELLABLE = 'unsellable'
}

// Interface for orders with status
interface WarehouseOrder {
  id: string;
  created_at: string;
  visionary_id: number | null;
  staff_id: number | null;
  total_amount: number;
  points_used: number;
  points_earned: number;
  staff: {
    id: number;
    full_name: string;
  } | null;
  visionary: {
    membership_number: number;
    name: string;
    email: string;
  } | null;
  order_items: {
    id: number;
    product_id: number;
    quantity: number;
    price_at_time: number;
    products: {
      id: number;
      name: string;
      platform: string | null;
      image_url: string;
      stock_quantity: number;
    };
  }[];
  status: OrderStatus;
  return_reason?: string;
  return_condition?: ItemCondition;
}

export default function Warehouse() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [orders, setOrders] = useState<WarehouseOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<OrderStatus | 'all'>('all');
  const [expandedOrder, setExpandedOrder] = useState<string | null>(null);
  const [processingAction, setProcessingAction] = useState<{orderId: string, action: string} | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedCondition, setSelectedCondition] = useState<ItemCondition>(ItemCondition.GOOD);

  // Check if user is admin or warehouse staff
  useEffect(() => {
    if (user) {
      const staffDetails = user.staff_details ? JSON.parse(user.staff_details) : null;
      
      if (!staffDetails || (staffDetails.role !== 'admin' && staffDetails.role !== 'warehouse_assistant')) {
        // Redirect if not authorized
        navigate('/dashboard');
      }
    }
  }, [user, navigate]);

  // Fetch all orders
  useEffect(() => {
    fetchOrders();
  }, [statusFilter]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      
      let query = supabase
        .from('orders')
        .select(`
          *,
          staff:staff_id (
            id,
            full_name
          ),
          visionary:visionary_id (
            membership_number,
            name,
            email
          ),
          order_items (
            id,
            product_id,
            quantity,
            price_at_time,
            products (
              id,
              name,
              platform,
              image_url,
              stock_quantity
            )
          )
        `)
        .order('created_at', { ascending: false });
      
      // Apply status filter if not 'all'
      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }
      
      const { data, error } = await query;

      if (error) throw error;
      setOrders(data || []);
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError('Failed to fetch orders. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Search functionality
  const filteredOrders = orders.filter(order => {
    if (!searchQuery) return true;
    
    const query = searchQuery.toLowerCase();
    return (
      order.id.toLowerCase().includes(query) ||
      (order.visionary?.email && order.visionary.email.toLowerCase().includes(query)) ||
      (order.visionary?.name && order.visionary.name.toLowerCase().includes(query))
    );
  });

  // Handle order status change
  const updateOrderStatus = async (orderId: string, newStatus: OrderStatus) => {
    try {
      setProcessingAction({ orderId, action: newStatus });
      setError(null);
      
      // Create update data
      let updateData: any = { status: newStatus };
      
      // If marking a return as received/refunded, add the condition
      if (newStatus === OrderStatus.REFUNDED) {
        updateData.return_condition = selectedCondition;
      }
      
      // Update order status in database
      const { error } = await supabase
        .from('orders')
        .update(updateData)
        .eq('id', orderId);
      
      if (error) throw error;
      
      // If cancelling or refunding an order, restore inventory
      if (newStatus === OrderStatus.CANCELLED || newStatus === OrderStatus.REFUNDED) {
        await restoreInventory(orderId);
      }
      
      // Update local state
      setOrders(prevOrders => prevOrders.map(order => 
        order.id === orderId ? { ...order, status: newStatus, ...updateData } : order
      ));
      
      setSuccess(`Order #${orderId} has been updated to ${newStatus}`);
      setTimeout(() => setSuccess(null), 3000);
      
    } catch (err) {
      console.error('Error updating order status:', err);
      setError('Failed to update order status. Please try again.');
    } finally {
      setProcessingAction(null);
    }
  };

  // Restore inventory when cancelling or refunding an order
  const restoreInventory = async (orderId: string) => {
    try {
      // Find the order
      const order = orders.find(o => o.id === orderId);
      if (!order) return;
      
      // For each item in the order, restore quantity to inventory
      for (const item of order.order_items) {
        // Update product stock quantity
        const { error } = await supabase
          .from('products')
          .update({ 
            stock_quantity: item.products.stock_quantity + item.quantity 
          })
          .eq('id', item.product_id);
        
        if (error) throw error;
      }
    } catch (err) {
      console.error('Error restoring inventory:', err);
      throw err;
    }
  };

  // Generate invoice PDF
  const generateInvoice = (order: WarehouseOrder) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    
    // Header with company details
    doc.setFontSize(28);
    doc.setTextColor(88, 28, 135); // Purple color for brand
    doc.text('Big Vision Games', pageWidth / 2, 25, { align: 'center' });
    
    doc.setFontSize(10);
    doc.setTextColor(128, 128, 128);
    doc.text('Gaming Beyond Boundaries', pageWidth / 2, 32, { align: 'center' });
    
    // Status tag
    const statusColor: Record<OrderStatus, string> = {
      [OrderStatus.PROCESSING]: '128, 90, 213', // Purple
      [OrderStatus.DISPATCHED]: '34, 197, 94',  // Green
      [OrderStatus.COMPLETE]: '34, 197, 94',    // Green
      [OrderStatus.CANCELLED]: '239, 68, 68',   // Red
      [OrderStatus.RETURN_INITIATED]: '245, 158, 11', // Amber
      [OrderStatus.REFUNDED]: '249, 115, 22'    // Orange
    };
    
    // Parse the color values
    const colorValues = statusColor[order.status].split(',').map(s => parseInt(s.trim()));
    // Apply the color values individually
    doc.setFillColor(colorValues[0], colorValues[1], colorValues[2]);
    doc.roundedRect(pageWidth - 80, 25, 60, 15, 3, 3, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.text(order.status.toUpperCase(), pageWidth - 50, 33, { align: 'center' });
    
    // Continue with regular invoice content...
    doc.setDrawColor(88, 28, 135);
    doc.setLineWidth(0.5);
    doc.line(20, 45, pageWidth - 20, 45);

    // Order and customer details
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    
    // Left column - Order details
    doc.text('Order Details:', 20, 60);
    doc.setFontSize(10);
    doc.text(`Order Number: ${order.id}`, 20, 70);
    doc.text(`Date: ${new Date(order.created_at).toLocaleDateString()}`, 20, 80);
    doc.text(`Status: ${order.status}`, 20, 90);
    
    // Right column - Customer details
    if (order.visionary) {
      doc.text('Customer:', pageWidth - 80, 60);
      doc.setFontSize(10);
      doc.text(`${order.visionary.name}`, pageWidth - 80, 70);
      doc.text(`ID: ${order.visionary.membership_number}`, pageWidth - 80, 80);
      doc.text(`${order.visionary.email}`, pageWidth - 80, 90);
    }
    
    // Items table
    const tableData = order.order_items.map(item => [
      item.product_id.toString(),
      `${item.products.name}${item.products.platform ? ` (${item.products.platform})` : ''}`,
      item.quantity.toString(),
      `£${item.price_at_time.toFixed(2)}`,
      `£${(item.price_at_time * item.quantity).toFixed(2)}`
    ]);

    autoTable(doc, {
      startY: 110,
      head: [['Product ID', 'Item Description', 'Qty', 'Unit Price', 'Total']],
      body: tableData,
      theme: 'grid',
      headStyles: { 
        fillColor: [88, 28, 135],
        textColor: [255, 255, 255]
      }
    });
    
    // Get the final Y position after the table
    const finalY = (doc as any).lastAutoTable.finalY + 20;
    
    // Order summary
    doc.text(`Subtotal: £${order.total_amount.toFixed(2)}`, pageWidth - 70, finalY);
    
    if (order.points_used > 0) {
      doc.text(`Points Used: ${order.points_used} (-£${(order.points_used/100).toFixed(2)})`, pageWidth - 70, finalY + 10);
    }
    
    if (order.points_earned > 0) {
      doc.text(`Points Earned: ${order.points_earned}`, pageWidth - 70, finalY + 20);
    }
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(`Total: £${order.total_amount.toFixed(2)}`, pageWidth - 70, finalY + 35);
    
    // Footer
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(128, 128, 128);
    doc.text('Warehouse Order Management', 20, doc.internal.pageSize.height - 20);
    doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth - 20, doc.internal.pageSize.height - 20, { align: 'right' });
    
    doc.save(`BVG-Order-${order.id}.pdf`);
  };

  // Get status badge style based on order status
  const getStatusBadgeStyle = (status: OrderStatus) => {
    switch (status) {
      case OrderStatus.PROCESSING:
        return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      case OrderStatus.DISPATCHED:
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case OrderStatus.COMPLETE:
        return 'bg-green-700/20 text-green-400 border-green-700/30';
      case OrderStatus.CANCELLED:
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      case OrderStatus.RETURN_INITIATED:
        return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
      case OrderStatus.REFUNDED:
        return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  if (loading && orders.length === 0) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 text-white animate-spin" />
      </div>
    );
  }

  return (
    <div className="fixed inset-x-0 bottom-0 top-16 overflow-y-auto">
      <div className="max-w-[90%] mx-auto p-8">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white">Warehouse Management</h1>
              <p className="text-white/60">Manage orders, dispatches, returns and inventory</p>
            </div>
            
            <div className="flex items-center gap-4">
              <button 
                onClick={fetchOrders} 
                className="px-4 py-2 bg-white/5 hover:bg-white/10 rounded-lg transition-colors text-white flex items-center gap-2"
              >
                <RotateCcw className="w-4 h-4" />
                Refresh
              </button>
            </div>
          </div>

          {/* Success and Error Messages */}
          {error && (
            <div className="p-4 bg-red-900/20 border border-red-500/30 rounded-lg text-red-400 flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 flex-shrink-0" />
              <p>{error}</p>
              <button 
                onClick={() => setError(null)} 
                className="ml-auto text-red-400/70 hover:text-red-400"
              >
                <XIcon className="w-4 h-4" />
              </button>
            </div>
          )}
          
          {success && (
            <div className="p-4 bg-green-900/20 border border-green-500/30 rounded-lg text-green-400 flex items-center gap-3">
              <Check className="w-5 h-5 flex-shrink-0" />
              <p>{success}</p>
              <button 
                onClick={() => setSuccess(null)} 
                className="ml-auto text-green-400/70 hover:text-green-400"
              >
                <XIcon className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Search and Filters */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-3 relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/40" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by order ID or customer email..."
                className="w-full pl-12 pr-4 py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all"
              />
            </div>
            
            <div className="relative">
              <Filter className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/40" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as OrderStatus | 'all')}
                className="w-full appearance-none pl-12 pr-4 py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all"
              >
                <option value="all">All Statuses</option>
                <option value={OrderStatus.PROCESSING}>Processing</option>
                <option value={OrderStatus.DISPATCHED}>Dispatched</option>
                <option value={OrderStatus.COMPLETE}>Complete</option>
                <option value={OrderStatus.CANCELLED}>Cancelled</option>
                <option value={OrderStatus.RETURN_INITIATED}>Return Initiated</option>
                <option value={OrderStatus.REFUNDED}>Refunded</option>
              </select>
            </div>
          </div>
        </div>

        {/* Orders List */}
        <div className="bg-gradient-to-b from-white/10 to-white/5 backdrop-blur-xl border border-white/20 rounded-xl overflow-hidden shadow-2xl">
          <div className="p-6 border-b border-white/10 flex justify-between items-center">
            <h2 className="text-xl font-semibold text-white">Orders</h2>
            <div className="text-white/50 text-sm">
              {filteredOrders.length} {filteredOrders.length === 1 ? 'order' : 'orders'} found
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1000px]">
              <thead className="bg-white/5 backdrop-blur-lg border-b border-white/10 sticky top-0 z-20">
                <tr>
                  <th className="py-4 px-6 text-left text-sm font-medium text-white/50">Order ID</th>
                  <th className="py-4 px-6 text-left text-sm font-medium text-white/50">Date</th>
                  <th className="py-4 px-6 text-left text-sm font-medium text-white/50">Customer</th>
                  <th className="py-4 px-6 text-left text-sm font-medium text-white/50">Items</th>
                  <th className="py-4 px-6 text-left text-sm font-medium text-white/50">Total</th>
                  <th className="py-4 px-6 text-left text-sm font-medium text-white/50">Status</th>
                  <th className="py-4 px-6 text-right text-sm font-medium text-white/50">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {filteredOrders.length > 0 ? (
                  filteredOrders.map((order) => (
                    <React.Fragment key={order.id}>
                      <tr 
                        className={`hover:bg-white/[0.03] transition-colors cursor-pointer ${
                          expandedOrder === order.id ? 'bg-white/[0.02]' : ''
                        }`}
                        onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
                      >
                        <td className="py-4 px-6">
                          <div className="font-medium text-white">{order.id}</div>
                          <div className="text-white/50 text-xs">
                            {order.staff ? `Processed by ${order.staff.full_name}` : 'Online order'}
                          </div>
                        </td>
                        <td className="py-4 px-6 text-white/70">
                          {new Date(order.created_at).toLocaleDateString()}
                          <div className="text-white/50 text-xs">
                            {new Date(order.created_at).toLocaleTimeString()}
                          </div>
                        </td>
                        <td className="py-4 px-6">
                          {order.visionary ? (
                            <div>
                              <div className="text-white">{order.visionary.name}</div>
                              <div className="text-white/50 text-xs">{order.visionary.email}</div>
                            </div>
                          ) : (
                            <div className="text-white/50">Guest order</div>
                          )}
                        </td>
                        <td className="py-4 px-6 text-white/70">
                          {order.order_items.length} item{order.order_items.length !== 1 ? 's' : ''}
                        </td>
                        <td className="py-4 px-6">
                          <div className="text-white font-medium">£{order.total_amount.toFixed(2)}</div>
                          {(order.points_used > 0 || order.points_earned > 0) && (
                            <div className="text-purple-400/80 text-xs">
                              {order.points_used > 0 && `-${order.points_used} pts`}
                              {order.points_used > 0 && order.points_earned > 0 && ' / '}
                              {order.points_earned > 0 && `+${order.points_earned} pts`}
                            </div>
                          )}
                        </td>
                        <td className="py-4 px-6">
                          <span className={`px-3 py-1 rounded-full text-xs border ${getStatusBadgeStyle(order.status)}`}>
                            {order.status}
                          </span>
                        </td>
                        <td className="py-4 px-6 text-right">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              generateInvoice(order);
                            }}
                            className="text-white/50 hover:text-white bg-white/5 hover:bg-white/10 p-2 rounded transition-colors"
                            title="Download Invoice"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                      
                      {/* Expanded Order View */}
                      {expandedOrder === order.id && (
                        <tr>
                          <td colSpan={7} className="p-0">
                            <div className="bg-white/[0.02] border-t border-white/5 p-6 overflow-auto max-h-[500px] scrollbar-thin scrollbar-thumb-purple-500/50 scrollbar-track-white/5">
                              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                {/* Order Items List */}
                                <div className="lg:col-span-2 space-y-4">
                                  <h3 className="text-lg font-medium text-white">Order Items</h3>
                                  
                                  <div className="grid gap-3">
                                    {order.order_items.map((item) => (
                                      <div 
                                        key={item.id}
                                        className="flex items-center gap-4 bg-white/5 p-4 rounded-lg"
                                      >
                                        <div className="w-16 h-16 rounded-md overflow-hidden bg-white/5">
                                          <img
                                            src={item.products.image_url}
                                            alt={item.products.name}
                                            className="w-full h-full object-cover"
                                          />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <h4 className="text-white font-medium truncate">
                                            {item.products.name}
                                          </h4>
                                          <div className="text-sm text-white/70 flex items-center gap-2">
                                            {item.products.platform && (
                                              <span className="px-2 py-0.5 bg-white/10 rounded text-white/70 text-xs">
                                                {item.products.platform}
                                              </span>
                                            )}
                                            <span>Quantity: {item.quantity}</span>
                                          </div>
                                        </div>
                                        <div className="text-right">
                                          <div className="text-white font-medium">
                                            £{(item.price_at_time * item.quantity).toFixed(2)}
                                          </div>
                                          <div className="text-white/50 text-sm">
                                            £{item.price_at_time.toFixed(2)} each
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                                
                                {/* Order Management */}
                                <div className="space-y-4">
                                  <h3 className="text-lg font-medium text-white">Manage Order</h3>
                                  
                                  <div className="bg-white/5 rounded-lg p-6 space-y-4">
                                    <div className="space-y-1">
                                      <h4 className="text-white/70 text-sm">Current Status</h4>
                                      <div className={`inline-block px-3 py-1 rounded-full text-sm border ${getStatusBadgeStyle(order.status)}`}>
                                        {order.status}
                                      </div>
                                    </div>
                                    
                                    {/* Display return reason for RETURN_INITIATED orders */}
                                    {order.status === OrderStatus.RETURN_INITIATED && (
                                      <div className="pt-3 pb-3 border-t border-white/10 mb-3">
                                        <h4 className="text-white/70 text-sm mb-2">Return Reason</h4>
                                        <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg text-white/80 text-sm">
                                          {order.return_reason || "No reason provided"}
                                        </div>
                                        
                                        <h4 className="text-white/70 text-sm mt-4 mb-2">Item Condition on Return</h4>
                                        <select 
                                          value={selectedCondition}
                                          onChange={(e) => setSelectedCondition(e.target.value as ItemCondition)}
                                          className="w-full bg-white/10 border border-white/20 rounded-lg p-2 text-white"
                                        >
                                          <option value={ItemCondition.EXCELLENT}>Excellent - Like New</option>
                                          <option value={ItemCondition.GOOD}>Good - Minor Wear</option>
                                          <option value={ItemCondition.ACCEPTABLE}>Acceptable - Used</option>
                                          <option value={ItemCondition.DAMAGED}>Damaged - Needs Repair</option>
                                          <option value={ItemCondition.UNSELLABLE}>Unsellable - Cannot be Resold</option>
                                        </select>
                                        
                                        <button
                                          onClick={() => updateOrderStatus(order.id, OrderStatus.REFUNDED)}
                                          className="w-full mt-3 flex items-center justify-center gap-2 px-3 py-3 bg-green-600/80 hover:bg-green-600 text-white rounded-lg transition-colors"
                                          disabled={processingAction?.orderId === order.id}
                                        >
                                          {processingAction?.orderId === order.id && processingAction?.action === OrderStatus.REFUNDED ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                          ) : (
                                            <CheckCircle className="w-4 h-4" />
                                          )}
                                          Confirm Return Received
                                        </button>
                                      </div>
                                    )}
                                    
                                    {/* Show refunded orders with condition information */}
                                    {order.status === OrderStatus.REFUNDED && order.return_reason && (
                                      <div className="pt-3 pb-1 border-t border-white/10">
                                        <h4 className="text-white/70 text-sm mb-2">Return Details</h4>
                                        <div className="grid grid-cols-2 gap-3">
                                          <div>
                                            <div className="text-white/60 text-xs">Reason</div>
                                            <div className="p-2 bg-white/5 border border-white/10 rounded-lg text-white/80 text-sm mt-1">
                                              {order.return_reason}
                                            </div>
                                          </div>
                                          <div>
                                            <div className="text-white/60 text-xs">Condition</div>
                                            <div className="p-2 bg-white/5 border border-white/10 rounded-lg text-white/80 text-sm mt-1">
                                              {order.return_condition || "Not recorded"}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Update Status section */}
                                    <div className="pt-4 border-t border-white/10 space-y-3">
                                      <h4 className="text-white/70 text-sm">Update Status</h4>
                                      
                                      {/* Only show dispatch button if processing */}
                                      {order.status === OrderStatus.PROCESSING && (
                                        <button
                                          onClick={() => updateOrderStatus(order.id, OrderStatus.DISPATCHED)}
                                          disabled={processingAction?.orderId === order.id}
                                          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600/80 hover:bg-green-600 text-white rounded-lg transition-colors"
                                        >
                                          {processingAction?.orderId === order.id && processingAction?.action === OrderStatus.DISPATCHED ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                          ) : (
                                            <TruckIcon className="w-4 h-4" />
                                          )}
                                          Mark as Dispatched
                                        </button>
                                      )}
                                      
                                      {/* Only show complete button if dispatched */}
                                      {order.status === OrderStatus.DISPATCHED && (
                                        <button
                                          onClick={() => updateOrderStatus(order.id, OrderStatus.COMPLETE)}
                                          disabled={processingAction?.orderId === order.id}
                                          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-700/80 hover:bg-green-700 text-white rounded-lg transition-colors"
                                        >
                                          {processingAction?.orderId === order.id && processingAction?.action === OrderStatus.COMPLETE ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                          ) : (
                                            <Check className="w-4 h-4" />
                                          )}
                                          Mark as Complete
                                        </button>
                                      )}

                                      {/* Action Buttons */}
                                      <div className="flex space-x-2 mt-4">
                                        {/* Show cancel button if not already cancelled or refunded */}
                                        {order.status !== OrderStatus.CANCELLED && order.status !== OrderStatus.REFUNDED && (
                                          <button
                                            onClick={() => updateOrderStatus(order.id, OrderStatus.CANCELLED)}
                                            className="flex items-center gap-1 px-3 py-2 bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/30 rounded transition-colors"
                                            disabled={processingAction?.orderId === order.id}
                                          >
                                            {processingAction?.orderId === order.id && processingAction?.action === OrderStatus.CANCELLED ? (
                                              <Loader2 className="w-4 h-4 animate-spin" />
                                            ) : (
                                              <X className="w-4 h-4" />
                                            )}
                                            Cancel
                                          </button>
                                        )}
                                        
                                        {/* Show refund button if not already refunded or cancelled or return initiated */}
                                        {order.status !== OrderStatus.REFUNDED && order.status !== OrderStatus.CANCELLED && order.status !== OrderStatus.RETURN_INITIATED && (
                                          <button
                                            onClick={() => updateOrderStatus(order.id, OrderStatus.REFUNDED)}
                                            className="flex items-center gap-1 px-3 py-2 bg-orange-500/20 text-orange-400 hover:bg-orange-500/30 border border-orange-500/30 rounded transition-colors"
                                            disabled={processingAction?.orderId === order.id}
                                          >
                                            {processingAction?.orderId === order.id && processingAction?.action === OrderStatus.REFUNDED ? (
                                              <Loader2 className="w-4 h-4 animate-spin" />
                                            ) : (
                                              <RotateCcw className="w-4 h-4" />
                                            )}
                                            Process Refund
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="py-8 text-center text-white/50">
                      {loading ? (
                        <div className="flex items-center justify-center">
                          <Loader2 className="w-6 h-6 animate-spin mr-2" />
                          Loading orders...
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center">
                          <Package className="w-10 h-10 mb-2 text-white/30" />
                          No orders found
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
} 